import { Injectable, inject } from "@angular/core";
import { HttpService } from "@app/core/services/http/http.service";
import { FormBuilder, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { Observable, map } from "rxjs";
import {
  FieldRulesAdapter,
  FieldRule,
  FormDataModelAdapter,
  FormDataModel,
  FieldRules
} from "@app/shared/models/form-field.model";
import { DateService } from "./date.service";

@Injectable({
  providedIn: "root"
})
export class FormService {
  private http = inject(HttpService);
  private dateService = inject(DateService);

  /**
   * fetchFieldRules()
   *
   * @deprecated: Use fetchDataModel() instead.
   * @param url
   */
  fetchFieldRules(url: string): Observable<FieldRule[]> {
    const fieldRulesAdapter: FieldRulesAdapter = new FieldRulesAdapter();
    return this.http
      .getModel()
      .get(url)
      .pipe(map((response: any) => fieldRulesAdapter.adapt(response)));
  }

  /**
   * fetchDataModel()
   *
   * Return FieldRules and FormDataModel objects from http request
   *
   * @param url
   */
  fetchDataModel(url: string): Observable<FormDataModel> {
    const formDataModelAdapter: FormDataModelAdapter = new FormDataModelAdapter();
    return this.http
      .getModel()
      .get(url)
      .pipe(map((response: any) => formDataModelAdapter.adapt(response)));
  }

  /**
   * formGroupBuilder()
   *
   * Build list of controls for a form group
   *
   * @param fieldRules
   * @param componentFormControls
   */
  formGroupBuilder(fieldRules: FieldRule[], componentFormControls?: object, customValidators?: object): FormGroup {
    const formControls = { ...componentFormControls };
    const formValidators = { ...customValidators };
    const formBuilder: FormBuilder = new FormBuilder();
    fieldRules.forEach(
      (fieldRule) =>
        (formControls[fieldRule.name] = formBuilder.control("", this._validatorBuilder(fieldRule, formValidators)))
    );

    return formBuilder.group(formControls);
  }

  /**
   * validatorBuilder()
   *
   * Build an array of validators for a control based on field rules returned from the server
   *
   * @param fieldRule
   * @param formValidators : custom validators like email address regex.
   */
  validatorBuilder(fieldRule: FieldRule | FieldRules, formValidators?: object): ValidatorFn[] {
    return this._validatorBuilder(fieldRule, formValidators);
  }

  /**
   * validatorBuilder()
   *
   * Build an array of validators for a control based on field rules returned from the server
   *
   * @param fieldRule
   */
  private _validatorBuilder(fieldRule: FieldRule | FieldRules, formValidators: object) {
    const validators = [];
    if (fieldRule.required) {
      validators.push(Validators.required);
    }

    if (fieldRule.regex) {
      validators.push(Validators.pattern(new RegExp(fieldRule.regex)));
    }

    if (fieldRule.minLength) {
      validators.push(Validators.minLength(fieldRule.minLength));
    }

    if (fieldRule.maxLength) {
      validators.push(Validators.maxLength(fieldRule.maxLength));
    }

    if (fieldRule.range) {
      validators.push(Validators.min(fieldRule.range.min as number));
      validators.push(Validators.max(fieldRule.range.max as number));

      // Do not apply decimal validation to date values
      if (!(this.dateService.isValidDate(fieldRule.range.min) && this.dateService.isValidDate(fieldRule.range.max))) {
        // Default to 0 decimal places
        let decExp = new RegExp(`^[0-9]*$`);

        if (fieldRule.range?.decimals > 0) {
          decExp = new RegExp(`^[0-9]+(.[0-9]{1,${fieldRule.range.decimals}})?$`);
        }

        validators.push(Validators.pattern(decExp));
      }
    }

    // Add any custom validator
    if (fieldRule instanceof FieldRule) {
      if (fieldRule?.name in formValidators) {
        let validator = formValidators[fieldRule.name];

        if (validator) {
          if (validator?.length === 1) {
            validator = [validator];
          }

          validators.push(...validator);
        }
      }
    }

    return validators;
  }
}
